import { useRef, useState } from "react";
import { Plus, Check, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useGet } from "../lib/hooks";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function Picker({ slot, onClose, title = "Page banner", shadeLabel = "Dark shade over the image" }) {
  const [assets, reload] = useGet("/assets?scope=account");
  const list = (assets || []).filter((a) => a.content_type !== "image/svg+xml");
  const cur = list.find((a) => a.slot === slot);
  const [shade, setShade] = useState(cur?.overlay ?? 50);
  const refresh = () => { reload(); window.dispatchEvent(new Event("branding-updated")); };
  const pick = async (a) => {
    try { await api.put(`/assets/${a.id}`, { slot, overlay: shade }); toast.success("Banner added"); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => {
    try { await api.put(`/assets/${cur.id}`, { slot: null }); toast.success("Banner removed"); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  const saveShade = async () => { if (cur) { try { await api.put(`/assets/${cur.id}`, { overlay: shade }); refresh(); } catch (e) { toast.error(errMsg(e)); } } };
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("name", file.name); fd.append("scope", "account");
      const r = await api.post("/assets", fd);
      await pick(r.data);
    } catch (e) { toast.error(errMsg(e)); } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="banner-picker">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="flex flex-wrap items-center justify-between gap-2"><p className="text-xs text-muted-foreground">Choose an image from this entity's Media Library, or upload a new one (it's saved to the Media Library too).</p>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="banner-upload-input" />
          <Button size="sm" variant="secondary" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="banner-upload-btn"><Upload className="mr-1 h-3.5 w-3.5" />{uploading ? "Uploading…" : "Upload image"}</Button></div>
        <div className="grid max-h-[340px] grid-cols-3 gap-2 overflow-auto sm:grid-cols-4">
          {list.map((a) => (
            <button key={a.id} onClick={() => pick(a)} className={`relative h-20 overflow-hidden rounded border ${a.slot === slot ? "border-[#C2A462] ring-1 ring-[#C2A462]" : "border-white/10 hover:border-[#C2A462]/60"}`} data-testid={`banner-pick-${a.id}`}>
              <img src={`${BACKEND}${a.url}`} alt={a.name} className="h-full w-full object-cover" />
              {a.slot === slot && <span className="absolute right-1 top-1 grid h-5 w-5 place-items-center rounded-full bg-[#C2A462] text-black"><Check className="h-3 w-3" /></span>}
            </button>))}
          {!list.length && <p className="col-span-full py-6 text-center text-sm text-muted-foreground">No images yet. Click Upload image to add one.</p>}
        </div>
        <div className="space-y-1">
          <div className="flex justify-between text-xs"><span className="text-muted-foreground">{shadeLabel}</span><span className="text-[#EBD6A0]" data-testid="banner-picker-shade-value">{shade}%</span></div>
          <input type="range" min={0} max={90} step={5} value={shade} onChange={(e) => setShade(Number(e.target.value))} onMouseUp={saveShade} onTouchEnd={saveShade} onKeyUp={saveShade} className="w-full accent-[#C2A462]" data-testid="banner-picker-shade" />
        </div>
        <div className="flex justify-between">
          {cur ? <Button variant="ghost" className="text-destructive" onClick={remove} data-testid="banner-remove">Remove banner</Button> : <span />}
          <Button variant="secondary" onClick={onClose} data-testid="banner-picker-done">Done</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function BannerPlus({ slot, className = "", title, shadeLabel, testid = "banner-plus" }) {
  const { user, account } = useAuth();
  const [open, setOpen] = useState(false);
  const allowed = slot && (user?.role === "agency_owner" || (account?.is_lab && user?.role !== "staff"));
  if (!allowed) return null;
  return <>
    <button onClick={() => setOpen(true)} title="Add or change the page banner" className={`grid h-5 w-5 place-items-center rounded-full border border-[#C2A462]/40 bg-black/30 text-[#C2A462]/70 opacity-60 transition-opacity hover:opacity-100 ${className}`} data-testid={testid}><Plus className="h-3 w-3" /></button>
    {open && <Picker slot={slot} title={title} shadeLabel={shadeLabel} onClose={() => setOpen(false)} />}
  </>;
}
